import React, { useCallback, useEffect, useState } from 'react';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loading, fmtDate } from '@/components/OpsUI';
import { Plus, Pencil, Trash2, BookOpen, Search, Eye, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SOURCES = {
  suite: { label: 'Suite', cls: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  app: { label: 'App', cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
  bomm: { label: 'BOMM', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
};

const EMPTY = { title: '', category: 'general', content: '', tags: '', enabled: true, source: 'bomm' };

export const ArticleDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial, tags: (initial.tags || []).join(', ') } : EMPTY);
  }, [open, initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    const body = { ...form, tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean) };
    try {
      if (initial?.id) await api.put(`/ai/kb/${initial.id}`, body);
      else await api.post('/ai/kb', body);
      toast.success('Artículo guardado');
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="kb-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? 'Editar artículo' : initial?.learned_from ? 'Guardar como conocimiento' : 'Nuevo artículo'}</DialogTitle>
          <DialogDescription>{initial?.learned_from ? `Borrador generalizado por la IA (origen ${form.source === 'app' ? 'App' : 'BOMM'}${initial.platform === 'suite' ? ', tema de la Suite' : ''}). Revísalo y edítalo antes de guardar.` : 'El asistente IA solo responde con lo que esté escrito en la base de conocimiento.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Título (ej. ¿Cómo agrego miembros?)" value={form.title} onChange={set('title')} data-testid="kb-title" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Categoría" value={form.category} onChange={set('category')} data-testid="kb-category" />
            <Input placeholder="Etiquetas separadas por coma" value={form.tags} onChange={set('tags')} data-testid="kb-tags" />
          </div>
          <Textarea rows={9} placeholder="Respuesta / guía paso a paso…" value={form.content} onChange={set('content')} data-testid="kb-content" />
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} data-testid="kb-enabled" />Activo (el agente lo usa)</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={busy || form.title.trim().length < 2 || form.content.trim().length < 5} data-testid="kb-save">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const KnowledgeBase = ({ canAct, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [src, setSrc] = useState('all');
  const [editing, setEditing] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [sync, setSync] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: s }] = await Promise.all([api.get('/ai/kb'), api.get('/ai/kb/sync')]);
      setItems(data || []); setSync(s);
    } catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChange?.(); };

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/ai/kb/sync');
      if (data.ok) toast.success(`Suite sincronizada: ${data.count} artículos`); else toast.error(`No se pudo sincronizar: ${data.error}`);
      refresh();
    } catch (e) { toast.error(errText(e)); } finally { setSyncing(false); }
  };

  const del = async () => {
    try { await api.delete(`/ai/kb/${toDelete.id}`); toast.success('Artículo eliminado'); refresh(); }
    catch (e) { toast.error(errText(e)); } finally { setToDelete(null); }
  };

  const counts = items.reduce((acc, a) => { const s = a.source || 'bomm'; acc[s] = (acc[s] || 0) + 1; return acc; }, {});
  const shown = items.filter((a) => (src === 'all' || (a.source || 'bomm') === src)
    && `${a.title} ${a.content} ${a.category} ${(a.tags || []).join(' ')}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <Input className="pl-9" placeholder="Buscar en la base…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="kb-search" />
        </div>
        <Select value={src} onValueChange={setSrc}>
          <SelectTrigger className="w-48" data-testid="kb-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los orígenes ({items.length})</SelectItem>
            {Object.entries(SOURCES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label} ({counts[k] || 0})</SelectItem>)}
          </SelectContent>
        </Select>
        {canAct && <Button onClick={() => { setEditing(null); setDlg(true); }} data-testid="kb-new"><Plus className="w-4 h-4 mr-2" />Nuevo artículo</Button>}
      </div>
      <Card className="p-3 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid="kb-sync-bar">
        <span className="flex items-center gap-2">
          <Badge variant="outline" className={SOURCES.suite.cls}>Suite</Badge>
          {sync?.last_sync
            ? <span data-testid="kb-sync-status">{sync.ok ? `${sync.count} artículos` : `Error: ${sync.error}`} · última sincronización {fmtDate(sync.last_sync)} · automática cada 6 h</span>
            : <span className="text-[hsl(var(--muted-foreground))]">Sin sincronizar todavía</span>}
        </span>
        {canAct && <Button size="sm" variant="outline" onClick={doSync} disabled={syncing} data-testid="kb-sync-btn"><RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />Sincronizar Suite</Button>}
      </Card>
      {loading ? <Loading /> : shown.length === 0 ? (
        <Card className="p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="kb-empty"><BookOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />Sin artículos</Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {shown.map((a) => {
            const s = SOURCES[a.source || 'bomm'];
            const editable = canAct && a.source !== 'suite';
            return (
            <Card key={a.id} className={`p-4 space-y-2 ${a.enabled ? '' : 'opacity-60'}`} data-testid={`kb-item-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <button type="button" className="font-medium text-left hover:text-[hsl(var(--primary))] transition-colors" onClick={() => setViewing(a)} data-testid={`kb-view-${a.id}`}>{a.title}</button>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => setViewing(a)} data-testid={`kb-open-${a.id}`}><Eye className="w-4 h-4" /></Button>
                  {editable && (
                    <>
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(a); setDlg(true); }} data-testid={`kb-edit-${a.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setToDelete(a)} data-testid={`kb-delete-${a.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                    </>
                  )}
                </div>
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] line-clamp-3 whitespace-pre-wrap">{a.content}</p>
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <Badge variant="outline" className={s.cls} data-testid={`kb-source-${a.id}`}>{s.label}</Badge>
                <Badge variant="outline">{a.category}</Badge>
                {a.kind === 'video' && <Badge variant="outline">Video</Badge>}
                {!a.enabled && <Badge variant="outline" className="text-slate-300">Inactivo</Badge>}
                {(a.tags || []).slice(0, 4).map((t) => <span key={t} className="text-[hsl(var(--muted-foreground))]">#{t}</span>)}
                <span className="ml-auto text-[hsl(var(--muted-foreground))]">{fmtDate(a.updated_at)}</span>
              </div>
            </Card>
            );
          })}
        </div>
      )}
      <ArticleDialog open={dlg} onOpenChange={setDlg} initial={editing} onSaved={refresh} />
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="kb-view-dialog">
          <DialogHeader>
            <DialogTitle>{viewing?.title}</DialogTitle>
            <DialogDescription>
              {viewing && `${SOURCES[viewing.source || 'bomm'].label} · ${viewing.category}`}
              {viewing?.source === 'suite' && ' · Solo lectura: se edita en la Suite Ministerial'}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm whitespace-pre-wrap leading-relaxed">{viewing?.content}</div>
          {viewing?.video_url && <a href={viewing.video_url} target="_blank" rel="noreferrer" className="text-sm text-[hsl(var(--primary))] underline" data-testid="kb-view-video">Ver microtutorial</a>}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar artículo?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.title}" dejará de usarse por el asistente IA.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="kb-delete-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
